/**
 * Strategy-specific config editor for a Mapping detail page.
 *
 * Renders one of three forms based on ``mapping.strategy_kind``.
 * On save, PATCHes the mapping's strategy_config. The parent owns
 * the success/error toast; we just expose ``onSaved``.
 *
 *   manual_table — nothing to configure (rows live in a separate
 *                  section). Renders a one-liner explaining that.
 *   db_lookup    — credential picker (postgres/mysql/sqlserver) +
 *                  query textarea + per-output column mapping +
 *                  on_multiple radio.
 *   http_lookup  — credential picker (http_bearer/api_key, optional)
 *                  + method/url_template + optional body + per-output
 *                  JSONata extractor + timeout.
 *
 * Form state mirrors the backend Pydantic schemas one-to-one. No
 * client-side enforcement of "complete" — we let the backend's
 * is_config_complete + PATCH validation be the source of truth (lets
 * the user save a partial config and come back later if needed).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import * as React from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CredentialPicker } from "@/components/credentials/CredentialPicker";
import {
  mappingsApi,
  type MappingField,
  type MappingSummary,
} from "@/lib/api-mappings";

export interface StrategyConfigEditorProps {
  mapping: MappingSummary;
  canEdit: boolean;
  onSaved?: () => void;
  /** Fired when the in-memory form diverges from the saved
   *  ``mapping.strategy_config`` (or comes back to match). The parent
   *  uses this to warn the user from the test panel below: "your
   *  test would run against the SAVED config; save above first". */
  onDirtyChange?: (dirty: boolean) => void;
}

export function StrategyConfigEditor(props: StrategyConfigEditorProps) {
  const { mapping, onDirtyChange } = props;
  // manual_table has no form state — it's never dirty. Notify the
  // parent on mount so a stale dirty flag from a previous strategy
  // doesn't carry over.
  React.useEffect(() => {
    if (mapping.strategy_kind === "manual_table") {
      onDirtyChange?.(false);
    }
  }, [mapping.strategy_kind, onDirtyChange]);

  if (mapping.strategy_kind === "manual_table") {
    return (
      <section>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Strategy config
        </h2>
        <p className="rounded border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          ``manual_table`` has no runtime config — the data lives in
          the Rows table below. Add / edit rows inline; the GIN index
          on ``inputs`` makes lookups O(log n) at workflow runtime.
        </p>
      </section>
    );
  }
  if (mapping.strategy_kind === "db_lookup") {
    return <DbLookupEditor {...props} />;
  }
  if (mapping.strategy_kind === "http_lookup") {
    return <HttpLookupEditor {...props} />;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────
// Shared save mutation hook
// ─────────────────────────────────────────────────────────────────────

function useConfigSaveMutation(
  mapping: MappingSummary,
  onSaved?: () => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (strategy_config: Record<string, unknown>) =>
      mappingsApi.update(mapping.id, { strategy_config }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mapping", mapping.id] });
      qc.invalidateQueries({ queryKey: ["mappings"] });
      onSaved?.();
    },
  });
}

// ─────────────────────────────────────────────────────────────────────
// db_lookup editor
// ─────────────────────────────────────────────────────────────────────

interface DbLookupCfg {
  credential_id?: string;
  query?: string;
  output_mapping?: Record<string, string>;
  on_multiple?: "fail" | "first";
}

function DbLookupEditor({ mapping, canEdit, onSaved, onDirtyChange }: StrategyConfigEditorProps) {
  const initial = (mapping.strategy_config ?? {}) as DbLookupCfg;
  const [credentialId, setCredentialId] = React.useState<string | null>(
    initial.credential_id ?? null,
  );
  const [query, setQuery] = React.useState<string>(initial.query ?? "");
  const [outputMapping, setOutputMapping] = React.useState<Record<string, string>>(
    initial.output_mapping ?? {},
  );
  const [onMultiple, setOnMultiple] = React.useState<"fail" | "first">(
    initial.on_multiple ?? "fail",
  );

  // Dirty detection — diff the current draft against the saved one
  // via JSON.stringify. Cheap, robust against key-order changes.
  // Notify the parent so the test panel below can warn.
  const dirty = React.useMemo(() => {
    const draft = {
      credential_id: credentialId ?? "",
      query,
      output_mapping: outputMapping,
      on_multiple: onMultiple,
    };
    const saved = {
      credential_id: initial.credential_id ?? "",
      query: initial.query ?? "",
      output_mapping: initial.output_mapping ?? {},
      on_multiple: initial.on_multiple ?? "fail",
    };
    return JSON.stringify(draft) !== JSON.stringify(saved);
  }, [credentialId, query, outputMapping, onMultiple, initial]);
  React.useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const save = useConfigSaveMutation(mapping, onSaved);

  function setOutMap(name: string, col: string) {
    setOutputMapping((m) => ({ ...m, [name]: col }));
  }
  function handleSave() {
    save.mutate({
      credential_id: credentialId ?? "",
      query,
      output_mapping: outputMapping,
      on_multiple: onMultiple,
    });
  }

  // Hint for what input placeholders to use in the query.
  const inputHint = mapping.inputs.map((i) => `:${i.name}`).join(", ");

  return (
    <section className="space-y-3">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Strategy config — Database lookup
      </h2>

      {save.error && (
        <Alert tone="danger">{(save.error as Error).message}</Alert>
      )}

      <div className="space-y-1">
        <Label htmlFor="dbl_cred">DB credential</Label>
        <CredentialPicker
          id="dbl_cred"
          type={["postgres", "mysql", "sqlserver"]}
          value={credentialId}
          onChange={setCredentialId}
          disabled={!canEdit}
          emptyText="No DB credentials — create a Postgres/MySQL/SQL Server credential first"
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="dbl_query">SQL query</Label>
        <textarea
          id="dbl_query"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={!canEdit}
          rows={6}
          spellCheck={false}
          placeholder="SELECT id, name FROM customers WHERE ext_code = :fluxo_code"
          className="w-full rounded border border-input bg-background px-2 py-1.5 font-mono text-xs"
        />
        <p className="text-[11px] text-muted-foreground">
          Must start with <code className="font-mono">SELECT</code> or{" "}
          <code className="font-mono">WITH</code>. Available placeholders:{" "}
          <span className="font-mono">{inputHint || "(no inputs declared)"}</span>
          . Read-only transaction enforced at runtime.
        </p>
      </div>

      <div className="space-y-1">
        <Label>Output mapping</Label>
        <p className="text-[11px] text-muted-foreground">
          For each declared output, the result-column alias to read.
        </p>
        <div className="rounded border border-border/60">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-2 py-1 text-left">Output</th>
                <th className="px-2 py-1 text-left">Type</th>
                <th className="px-2 py-1 text-left">SQL column / alias</th>
              </tr>
            </thead>
            <tbody>
              {mapping.outputs.map((f: MappingField) => (
                <tr key={f.name} className="border-t border-border/60">
                  <td className="px-2 py-1 font-mono text-xs">{f.name}</td>
                  <td className="px-2 py-1 font-mono text-[10px] text-muted-foreground">
                    {f.type}
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      value={outputMapping[f.name] ?? ""}
                      onChange={(e) => setOutMap(f.name, e.target.value)}
                      placeholder={f.name}
                      disabled={!canEdit}
                      className="h-7 font-mono text-xs"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-1">
        <Label>If query returns multiple rows</Label>
        <div className="flex items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-1.5">
            <input
              type="radio"
              checked={onMultiple === "fail"}
              onChange={() => setOnMultiple("fail")}
              disabled={!canEdit}
            />
            Fail (safe default)
          </label>
          <label className="inline-flex items-center gap-1.5">
            <input
              type="radio"
              checked={onMultiple === "first"}
              onChange={() => setOnMultiple("first")}
              disabled={!canEdit}
            />
            Take the first
          </label>
        </div>
      </div>

      {canEdit && (
        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" strokeWidth={1.5} />
            )}
            Save strategy config
          </Button>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// http_lookup editor
// ─────────────────────────────────────────────────────────────────────

interface HttpLookupCfg {
  credential_id?: string | null;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  url_template?: string;
  body_template?: string | null;
  output_extractors?: Record<string, string>;
  timeout_seconds?: number;
}

function HttpLookupEditor({ mapping, canEdit, onSaved, onDirtyChange }: StrategyConfigEditorProps) {
  const initial = (mapping.strategy_config ?? {}) as HttpLookupCfg;
  const [credentialId, setCredentialId] = React.useState<string | null>(
    initial.credential_id ?? null,
  );
  const [method, setMethod] = React.useState<NonNullable<HttpLookupCfg["method"]>>(
    initial.method ?? "GET",
  );
  const [urlTemplate, setUrlTemplate] = React.useState(initial.url_template ?? "");
  const [bodyTemplate, setBodyTemplate] = React.useState<string>(
    initial.body_template ?? "",
  );
  const [extractors, setExtractors] = React.useState<Record<string, string>>(
    initial.output_extractors ?? {},
  );
  const [timeout, setTimeout_] = React.useState<number>(
    initial.timeout_seconds ?? 10,
  );

  // Same dirty-detection contract as DbLookupEditor — see comment there.
  const dirty = React.useMemo(() => {
    const draft = {
      credential_id: credentialId,
      method,
      url_template: urlTemplate,
      body_template: bodyTemplate,
      output_extractors: extractors,
      timeout_seconds: timeout,
    };
    const saved = {
      credential_id: initial.credential_id ?? null,
      method: initial.method ?? "GET",
      url_template: initial.url_template ?? "",
      body_template: initial.body_template ?? "",
      output_extractors: initial.output_extractors ?? {},
      timeout_seconds: initial.timeout_seconds ?? 10,
    };
    return JSON.stringify(draft) !== JSON.stringify(saved);
  }, [credentialId, method, urlTemplate, bodyTemplate, extractors, timeout, initial]);
  React.useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const save = useConfigSaveMutation(mapping, onSaved);

  const hasBody = method !== "GET" && method !== "DELETE";

  function setExt(name: string, expr: string) {
    setExtractors((e) => ({ ...e, [name]: expr }));
  }
  function handleSave() {
    save.mutate({
      // null is the wire shape for "no auth"; empty string would
      // fail the credential UUID format check.
      credential_id: credentialId || null,
      method,
      url_template: urlTemplate,
      body_template: hasBody && bodyTemplate ? bodyTemplate : null,
      output_extractors: extractors,
      timeout_seconds: timeout,
    });
  }

  const inputHint = mapping.inputs.map((i) => `{${i.name}}`).join(", ");

  return (
    <section className="space-y-3">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Strategy config — HTTP lookup
      </h2>

      {save.error && (
        <Alert tone="danger">{(save.error as Error).message}</Alert>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-[120px_1fr]">
        <div className="space-y-1">
          <Label htmlFor="http_method">Method</Label>
          <select
            id="http_method"
            value={method}
            onChange={(e) =>
              setMethod(e.target.value as NonNullable<HttpLookupCfg["method"]>)
            }
            disabled={!canEdit}
            className="h-9 w-full rounded border border-input bg-background px-2 text-sm"
          >
            {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="http_url">URL template</Label>
          <Input
            id="http_url"
            value={urlTemplate}
            onChange={(e) => setUrlTemplate(e.target.value)}
            disabled={!canEdit}
            placeholder="https://erp.acme.com/customers/{fluxo_code}"
            className="font-mono text-xs"
          />
          <p className="text-[11px] text-muted-foreground">
            Placeholders: <span className="font-mono">{inputHint || "(no inputs)"}</span>{" "}
            (URL-encoded at substitution time).
          </p>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="http_cred">Auth credential (optional)</Label>
        <CredentialPicker
          id="http_cred"
          type={["http_bearer", "api_key"]}
          value={credentialId}
          onChange={setCredentialId}
          disabled={!canEdit}
          placeholder="none (anonymous request)"
        />
      </div>

      {hasBody && (
        <div className="space-y-1">
          <Label htmlFor="http_body">Body template</Label>
          <textarea
            id="http_body"
            value={bodyTemplate}
            onChange={(e) => setBodyTemplate(e.target.value)}
            disabled={!canEdit}
            rows={4}
            spellCheck={false}
            placeholder={`{"code": {fluxo_code}, "scope": "lookup"}`}
            className="w-full rounded border border-input bg-background px-2 py-1.5 font-mono text-xs"
          />
          <p className="text-[11px] text-muted-foreground">
            Placeholders are JSON-encoded — strings get proper quoting
            inside JSON bodies. Optional.
          </p>
        </div>
      )}

      <div className="space-y-1">
        <Label>Output extractors</Label>
        <p className="text-[11px] text-muted-foreground">
          One JSONata expression per declared output, evaluated against the JSON response body.
        </p>
        <div className="rounded border border-border/60">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-2 py-1 text-left">Output</th>
                <th className="px-2 py-1 text-left">Type</th>
                <th className="px-2 py-1 text-left">JSONata</th>
              </tr>
            </thead>
            <tbody>
              {mapping.outputs.map((f: MappingField) => (
                <tr key={f.name} className="border-t border-border/60">
                  <td className="px-2 py-1 font-mono text-xs">{f.name}</td>
                  <td className="px-2 py-1 font-mono text-[10px] text-muted-foreground">
                    {f.type}
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      value={extractors[f.name] ?? ""}
                      onChange={(e) => setExt(f.name, e.target.value)}
                      placeholder="data.id"
                      disabled={!canEdit}
                      className="h-7 font-mono text-xs"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-1 max-w-[200px]">
        <Label htmlFor="http_timeout">Timeout (seconds)</Label>
        <Input
          id="http_timeout"
          type="number"
          min={0.5}
          max={60}
          step={0.5}
          value={timeout}
          onChange={(e) => setTimeout_(Number(e.target.value))}
          disabled={!canEdit}
        />
      </div>

      {canEdit && (
        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" strokeWidth={1.5} />
            )}
            Save strategy config
          </Button>
        </div>
      )}
    </section>
  );
}
